import { Typography, Grid, Button, Table, Divider, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import {
	ApplicantInfo,
	ApplicantReference,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { useWizard } from '../../../components/wizard/useWizard';
import { useFamilyMemberInterviewWizard } from './useFamilyMemberInterviewWizard';
import { useEffect } from 'react';
import { ApplicantDetailsTable } from '../ApplicantDetailsTable';
import { useInterviewLandingPage } from '../InterviewLandingPage/useInterviewLandingPage';
import {
	INTERVIEW_LANDING,
	FAMILY_MEMBER_INTERVIEW,
} from '../../../shared/Routes';
import Icon from '../../../components/ui-components/Icon';
import LeftArrowWhite from '../../../assets/images/arrow-left-white.svg';
import RightArrowWhite from '../../../assets/images/arrow-right-white.svg';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface LandingPageProps {
	applicantInfo?: ApplicantInfo;
	familyReference?: ApplicantReference;
}

const SectionHeader = ({ heading }: { heading: string }) => {
	const { t } = useTranslation();
	const { navigateToPage } = useInterviewLandingPage();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;

	return (
		<div className="relative flex items-center justify-between">
			{!isMobile && (
				<Button
					type="primary"
					onClick={() => navigateToPage(INTERVIEW_LANDING)}
					icon={<Icon src={LeftArrowWhite} width={'w-4'} height={'h-4'} />}
					className="absolute left-0 font-bold text-sm"
				>
					{t('back')}
				</Button>
			)}
			<Text className="flex-grow text-center font-sans font-semibold text-lg text-secondary-black my-4">
				{heading}
			</Text>
		</div>
	);
};

export const LandingPage = ({
	applicantInfo,
	familyReference,
}: LandingPageProps) => {
	const { t } = useTranslation();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const { navigateToPage } = useInterviewLandingPage();

	const familyReferenceTableColumns = [
		{
			title: t('name'),
			dataIndex: 'name',
			key: 'name',
			width: '15%',
			className: 'applicant-table-row',
		},
		{
			title: t('relationship'),
			dataIndex: 'relationship',
			key: 'relationship',
			width: '15%',
			render: (text: string) => (
				<p>{text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()}</p>
			),
			className: 'applicant-table-row',
		},
		{
			title: t('mobile'),
			dataIndex: 'mobile',
			key: 'mobile',
			width: '15%',
			className: 'applicant-table-row',
		},
	];

	const data = familyReferenceTableColumns
		? [{ key: '1', ...familyReference }]
		: [];

	if (!familyReference) {
		return (
			<div className="flex flex-col justify-center items-center gap-5">
				<Text className="flex-grow text-center font-sans font-semibold text-lg text-secondary-black mt-4">
					{t('familyContactDetailsAreLoading')}
				</Text>
				<Spin tip="Loading" size="large" />
			</div>
		);
	}

	return (
		<div className="flex justify-center">
			<div
				className={`flex flex-col h-full min-h-0 p-[8px] ${
					isMobile ? 'w-full' : 'w-[90%]'
				}`}
			>
				<SectionHeader heading={t('familyMemberInterview')} />

				<ApplicantDetailsTable applicantInfo={applicantInfo} />

				<Text className="flex-grow text-center font-sans font-semibold text-base text-secondary-black mt-6">
					{t('familyMemberDetails')}
				</Text>

				{!isMobile && (
					<div className="max-w-3xl w-full mx-auto">
						<Table
							columns={familyReferenceTableColumns}
							dataSource={data}
							pagination={false}
							className="my-4 text-sm rounded-md border border-solid border-gray-200"
							showHeader
							size="small"
							scroll={{ x: true }}
						/>
					</div>
				)}

				{isMobile && (
					<div className="grid grid-cols-2 gap-4 p-4 bg-white rounded-lg border border-solid border-gray-200">
						{familyReferenceTableColumns.map((col) => (
							<div key={col.dataIndex} className="flex flex-col">
								<Text strong>{col.title}</Text>
								<Text>
									{familyReference?.[
										col.dataIndex as keyof ApplicantReference
									] || '-'}
								</Text>
							</div>
						))}
					</div>
				)}

				<div className="flex justify-center">
					<div
						className={`flex flex-col items-center bg-white p-4 rounded-lg shadow-md my-4 ${
							!isMobile ? 'w-3/4' : 'w-full'
						}`}
					>
						<Text className="text-base font-semibold text-center mt-2 text-secondary-black">
							{t('setContextPolitely')}
						</Text>
						<Divider className="border border-solid border-gray-200" />
						<div className="font-sans font-semibold text-sm text-gray-650 px-4">
							<p className="font-extrabold">
								{t('dearProposer')}
								{','}
							</p>
							<p>{t('kindlyFollowGuidelinesForFamilyMember')}</p>
							<p>
								<b>
									{'1. '}
									{t('schedulingInterviews')}
								</b>
								<div className="ml-4">
									<li>{t('avoidColdCallsToFamilyMember')}</li>
									<li>{t('contactFamilyMember')}</li>
									<li>{t('videoCallsFamilyMember')}</li>
								</div>
							</p>
							<p>
								<b>
									{'2. '}
									{t('introScript')}
								</b>
								<p className="mt-1">{t('useTheFollowing')}</p>
								<p>
									{'"'}
									{t('familyMemberMayITalkToYou')}
									{'"'}
								</p>
							</p>
							<p>
								<b>
									{'3. '}
									{t('documentResponses')}
								</b>
								<div className="ml-4">
									<li>{t('docFamilyMemberResponse')}</li>
									<li>{t('maintainObjectivity')}</li>
								</div>
							</p>
						</div>
						<div
							className={`flex ${
								!isMobile ? 'justify-end' : 'justify-center'
							} w-full gap-2 mt-4`}
						>
							<Button
								onClick={() => navigateToPage(INTERVIEW_LANDING)}
								className="font-bold text-sm"
							>
								{t('cancel')}
							</Button>
							<Button
								type="primary"
								onClick={() => navigateToPage(FAMILY_MEMBER_INTERVIEW)}
								className="font-bold text-sm"
							>
								{t('next')}
								<Icon src={RightArrowWhite} width={'w-5'} height={'h-5'} />
							</Button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export const LandingPageContainer = () => {
	const { setNextButtonDisabled } = useWizard();

	const { application } = useFamilyMemberInterviewWizard();

	useEffect(() => {
		if (!application?.familyMemberReference) {
			setNextButtonDisabled(true);
		} else {
			setNextButtonDisabled(false);
		}
	}, [
		application?.familyMemberReference,
		application.preceptorReference,
		setNextButtonDisabled,
	]);

	return (
		<LandingPage
			applicantInfo={application?.applicant}
			familyReference={application?.familyMemberReference}
		/>
	);
};
